import * as Sentry from '@sentry/nextjs'
import { Criticality, type IdentifierFormats, type Logging, LoggingUser, Severity } from 'types/logging'
import { snakeCase } from 'es-toolkit/string'

/**
 * Associates a user with subsequent client Sentry events for the current browser session.
 *
 * @param {LoggingUser} user - The user to associate with Sentry events.
 * @returns {void}
 * @example
 * setLogUser({ id: '123', email: 'user@example.com', name: 'Jane' })
 */
export const setLogUser = (user: LoggingUser): void => {
  // Skip setting user in server environments to prevent cross-request user attribution leaks
  if (typeof window === 'undefined') return

  Sentry.setUser({ id: user.id ?? user.email, email: user.email, username: user.name })
}

/**
 * Attaches arbitrary key-value data to a named context section in Sentry.
 * Useful for capturing structured state (e.g. form values, API responses) alongside errors.
 *
 * @param {string} section - The name of the context section in Sentry.
 * @param {Record<string, unknown>} data - The key-value data to attach to the section.
 * @returns {void}
 * @example
 * setLogContext('formData', { email: 'user@example.com', ean: '123456789' })
 */
export const setLogContext = (section: string, data: Record<string, unknown>): void => {
  Sentry.setContext(snakeCase(section), data)
}

/**
 * Appends a breadcrumb to Sentry's trail, providing an audit log of events leading up to an error.
 *
 * @param {string} message - A short description of the event.
 * @param {Record<string, unknown>} [metadata] - Optional additional data to attach to the breadcrumb.
 * @returns {void}
 * @example
 * addLogBreadcrumb('User submitted registration form', { step: 'address' })
 */
export const addLogBreadcrumb = (message: string, metadata?: Record<string, unknown>): void => {
  Sentry.addBreadcrumb({ message, data: metadata })
}

/**
 * Extracts the flow name from a structured identifier string of the form `[FLOW_NAME:...]` or `[FLOW_NAME]`.
 * Returns `'unknown'` when the identifier doesn't match the expected format.
 *
 * @param {string} identifier - A structured log identifier, e.g. `[simulation:createProspect]`.
 * @returns {string} The lowercase flow name, or `'unknown'` if parsing fails.
 * @example
 * parseFlowFromIdentifier('[simulation:createProspect]') // 'simulation'
 * parseFlowFromIdentifier('[CMS:career:getStaticProps]') // 'cms'
 * parseFlowFromIdentifier('invalid')                    // 'unknown'
 */
export const parseFlowFromIdentifier = (identifier: string): string => {
  const match = identifier.match(/^\[([A-Za-z0-9_]+)(?::|])/)
  return match?.[1]?.toLowerCase() ?? 'unknown'
}

/**
 * Tracks a count metric in Sentry. This is useful for tracking the number of times a certain event occurs, such as the
 * number of times a specific error is thrown.
 *
 * @param {string} metric
 * @param {Record<string, string | number | boolean>} attributes
 */
export const trackSentryCount = (metric: string, attributes?: Record<string, string | number | boolean>): void => {
  Sentry.metrics.count(metric, 1, { attributes })
}

/**
 * Captures an exception in Sentry, tagged with the flow, criticality, and identifier derived from the logging payload.
 *
 * @template {IdentifierFormats} T
 * @param {Logging<T>} params - The logging payload.
 * @param {Error | string} params.error - The error to capture. Strings are wrapped in an `Error`. Its message is part
 * of the Sentry fingerprint, so keep it constant and pass variable values (ids, URLs, codes) through `metaData`.
 * @param {T} params.identifier - A structured identifier string that encodes the flow and location, e.g. `[simulation:createProspect]`.
 * @param {Severity} [params.severity=Severity.ERROR] - The Sentry severity level.
 * @param {Criticality} [params.criticality=Criticality.MEDIUM] - Business criticality tag used for alerting triage.
 * @param {{ [section: string]: { [key: string]: unknown } }} [params.metaData] - Optional structured context data, keyed by section name.
 * @param {Record<string, string>} [params.tags] - Optional searchable tags, e.g. an id shared with a related event.
 * @returns {void}
 * @example
 * log({
 *   identifier: '[simulation:createProspect]',
 *   error: new Error('Failed to create prospect'),
 *   severity: Severity.ERROR,
 *   criticality: Criticality.HIGH,
 *   metaData: { simulationRequestData: { ean: '123' } },
 * })
 */
export const log = <T extends IdentifierFormats>({
  error,
  metaData,
  identifier,
  tags,
  severity = Severity.ERROR,
  criticality = Criticality.MEDIUM
}: Logging<T>): void => {
  const err = typeof error === 'string' ? new Error(error) : error
  const flow = parseFlowFromIdentifier(identifier)

  Sentry.withScope((scope) => {
    scope.setTag('flow', flow)
    scope.setTag('criticality', criticality)
    scope.setTag('identifier', identifier)
    scope.setLevel(severity)

    if (tags) {
      Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value))
    }

    // Sentry's default grouping fails here in both directions: a string error is wrapped in an Error above, so all of
    // them share this function's stack trace and collapse into one catch-all issue, while a real Error groups on a
    // minified stack whose frame names change every build, so one call site fragments into a new issue per release.
    // This is what makes the message part of the contract: keep it constant and pass anything variable via metaData.
    scope.setFingerprint([identifier, err?.message || err?.name || 'unknown'])

    if (metaData) {
      Object.entries(metaData).forEach(([section, data]) => scope.setContext(section, data))
    }
    Sentry.captureException(err)
  })
}
